// The best lineup a roster could have started, by what each player scored.
// PLAN-2026-09-26-season-end S2: "left on the bench" (AWARDS.md) and the
// recap's bench section (RECAP.md) are the gap between this and the score.
//
// MOST RESTRICTIVE SLOT FIRST, each taking the highest scorer it accepts who is
// still free. For slots whose eligibility nests — a dedicated slot's positions
// inside a flex's, a flex's inside a superflex's — that is the best lineup,
// because a player a narrow slot takes could only have been worth as much
// anywhere wider. It is how Sleeper, ESPN and Yahoo compute "max points", and
// the trade read-out's `bestLineup` makes the same walk. Pure and sport-free:
// the slots and each player's positions are handed in.

export interface BestLineupSlot {
  id: string;
  count: number;
  /** Only `starter` slots are filled. */
  kind: string;
  eligible: readonly string[];
}

export interface BestLineupPlayer {
  id: string;
  positions: readonly string[];
  points: number;
  /**
   * Who goes first among players level on points, lowest first: in best ball
   * the starters as they are stored, then the bench, so a week where nobody
   * has played yet keeps the lineup a manager left. Absent is 0.
   */
  rank?: number;
}

export interface BestLineup {
  total: number;
  starters: { slot: string; playerId: string; points: number }[];
}

export function bestLineup(
  slots: readonly BestLineupSlot[],
  players: readonly BestLineupPlayer[],
): BestLineup {
  const starters = slots
    .filter((s) => s.kind === 'starter')
    .map((s, order) => ({ s, order }))
    .sort((a, b) => a.s.eligible.length - b.s.eligible.length || a.order - b.order)
    .map((x) => x.s);
  const free = [...players].sort(
    (a, b) => b.points - a.points || (a.rank ?? 0) - (b.rank ?? 0) || a.id.localeCompare(b.id),
  );
  const taken = new Set<string>();
  const out: BestLineup['starters'] = [];
  let total = 0;
  for (const slot of starters) {
    for (let i = 0; i < slot.count; i += 1) {
      const best = free.find(
        (p) => !taken.has(p.id) && p.positions.some((pos) => slot.eligible.includes(pos)),
      );
      if (best === undefined) break;
      taken.add(best.id);
      out.push({ slot: slot.id, playerId: best.id, points: best.points });
      total += best.points;
    }
  }
  return { total: Math.round(total * 100) / 100, starters: out };
}
